import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'antd';
import PropTypes from 'prop-types';

import ProgressUpdateInput from './ProgressUpdateInput';

import { updateProgress } from '../../reports/state/reports.actions';

import { getProgressFromState } from './progressHelpers';

export default function ProjectSpendUpdate({
  projectId,
  phaseId,
  costcodeId,
  hasWrite,
  activeCategoryId, // Set to a specific category ID for multi-category costcode rows
}) {
  const dispatch = useDispatch();
  const progressState = useSelector((state) => state.reports.progress);

  const {
    projectCostcodeDetails: taskState,
    projectProgressFilters,
  } = useSelector((state) => state.projects);

  // Determine if this is a custom category (string ID) or default category (number ID)
  const isCustomCategory = typeof activeCategoryId === 'string';
  const defaultCategoryId = activeCategoryId && !isCustomCategory ? activeCategoryId : null;
  const customCategoryId = activeCategoryId && isCustomCategory ? activeCategoryId : null;

  const { actualSpend } = useMemo(() => (
    getProgressFromState({
      progressState,
      taskState,
      projectId,
      phaseId,
      costcodeId,
      defaultCategoryId,
      customCategoryId,
    })
  ), [
    progressState,
    taskState,
    projectId,
    phaseId,
    costcodeId,
    defaultCategoryId,
    customCategoryId,
  ]);

  const [spend, setSpend] = useState(actualSpend);
  const [pastSpend, setPastSpend] = useState(actualSpend);

  const [showPopover, setShowPopover] = useState(false);
  const onChange = useCallback((newValue) => {
    setShowPopover(true);
    setSpend(newValue);
  }, []);

  const onConfirm = useCallback(async () => {
    if (await dispatch(updateProgress({
      projectId,
      phaseId,
      costcodeId,
      actualSpend: spend,
      defaultCategoryId,
      customCategoryId,
    }))) {
      setPastSpend(spend);
      setShowPopover(false);
    }
  }, [spend, dispatch, projectId, phaseId, costcodeId, defaultCategoryId, customCategoryId]);

  const onCancel = useCallback(() => {
    setSpend(pastSpend);
    setShowPopover(false);
  }, [pastSpend]);

  useEffect(() => {
    setSpend(actualSpend);
    setPastSpend(actualSpend);

    setTimeout(() => {
      setShowPopover(false);
    });
  }, [actualSpend]);

  return (
    <Row style={{ height: 32 }} align="middle">
      {
        hasWrite
          ? (
            <ProgressUpdateInput
              min={Number.MIN_SAFE_INTEGER}
              title="Update Actual Spend?"
              onConfirm={onConfirm}
              onCancel={onCancel}
              onChange={onChange}
              showPopover={showPopover}
              value={spend}
              formatter={(val) => `$ ${val}`}
              disabled={!!projectProgressFilters?.dateRange}
            />
          )
          : `$ ${spend}`
      }
    </Row>
  );
}

ProjectSpendUpdate.propTypes = {
  projectId: PropTypes.string,
  phaseId: PropTypes.string,
  costcodeId: PropTypes.string,
  hasWrite: PropTypes.bool,
  activeCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ProjectSpendUpdate.defaultProps = {
  projectId: undefined,
  phaseId: undefined,
  costcodeId: undefined,
  hasWrite: true,
  activeCategoryId: undefined,
};
